import { useT } from '../i18n/messages';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, X, XCircle } from 'lucide-react';

import { Button } from './ui';

type ToastKind = 'success' | 'error';

interface ToastEntry {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastContextValue {
  error(message: string): void;
  success(message: string): void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

function ToastItem({ onDismiss, toast }: { onDismiss: (id: number) => void; toast: ToastEntry }) {
  const t = useT();
  useEffect(() => {
    const timer = window.setTimeout(() => onDismiss(toast.id), 5000);
    return () => window.clearTimeout(timer);
  }, [onDismiss, toast.id]);

  const Icon = toast.kind === 'success' ? CheckCircle2 : XCircle;

  return (
    <div
      className={`toast toast--${toast.kind}`}
      role={toast.kind === 'error' ? 'alert' : 'status'}
    >
      <Icon aria-hidden="true" className="toast__icon" />
      <p>{toast.message}</p>
      <Button
        aria-label={t('billing.ui.dismiss.notification')}
        className="toast__close"
        onClick={() => onDismiss(toast.id)}
        type="button"
        variant="ghost"
      >
        <X aria-hidden="true" className="toast__close-icon" />
      </Button>
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const notify = useCallback((kind: ToastKind, message: string) => {
    setToasts((current) => [
      ...current,
      {
        id: Date.now() + Math.floor(Math.random() * 1000),
        kind,
        message,
      },
    ]);
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      error: (message) => notify('error', message),
      success: (message) => notify('success', message),
    }),
    [notify],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined'
        ? createPortal(
            <div className="toast-region" aria-live="polite" aria-relevant="additions">
              {toasts.map((toast) => (
                <ToastItem key={toast.id} onDismiss={dismiss} toast={toast} />
              ))}
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider.');
  }
  return context;
}
